import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { AdherenceIndexSummary, AdherenceRow } from "@/api/diagnostic";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import { cn } from "@/lib/utils";

import { AdherenceBar } from "../../adherence/AdherenceBar";
import { PreliminaryBadge } from "../../adherence/PreliminaryBadge";
import { adherenceEnvelope, adherenceIndex } from "../../data/adherenceIndex";
import { DiagnosticSection, type DiagnosticTabProps } from "../../DiagnosticSection";
import { ADHERENCE_BAR, ADHERENCE_COLORS } from "../../diagnosticMeta";
import { NO_VALUE } from "../../sectionState";

/** O slug do drill-down de três abas em `DIAGNOSTIC_DETAILS`. */
const DETAIL = "adherence";

/**
 * O frame "Aderência ao padrão" do Ato 1 (EF-FUNC-011, RF-07 e RF-09), logo
 * abaixo da visão geral, onde o tile só dá o número do consolidado.
 *
 * As três linhas da §3.4 — Processo de negócio, Desenvolvimentos e Consolidado —
 * com a barra e "quantidade · %" por cor, a frase para o cliente e a nota do que
 * ficou fora da conta. Tudo vem do resumo do índice, inclusive a composição da
 * linha Processo e os WRICEF que contam dentro do scope item: a tela não
 * reconta, e o PDF e o Resumo dizem o mesmo.
 *
 * Os estados sem índice (vazio, falha, não coletado) ficam com a casca do
 * `DiagnosticSection`, como os outros cards: nunca 0% inventado.
 */
export function AdherenceCard({ validation, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const envelope = adherenceEnvelope(validation);
  const state = adherenceIndex(validation);
  const index = state.kind === "counted" ? state.index : undefined;

  return (
    <div data-testid="adherence-card">
      <DiagnosticSection
        envelope={envelope}
        title={t("Standard fit")}
        subtitle={t(
          "Validated scope items and the client's developments, classified by what runs in the Public standard, with a Clean Core extension or with a workaround.",
        )}
        snapshots={snapshots}
        showsSampling={false}
        actions={
          index && (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3">
              {index.preliminary && (
                <Link to={undecidedDetail(index)} className="hover:opacity-80">
                  <PreliminaryBadge undecided={index.undecided_count} />
                </Link>
              )}
              <Link
                to={DETAIL}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
              >
                {t("See detail")}
                <ArrowRight aria-hidden className="h-3 w-3" />
              </Link>
            </div>
          )
        }
      >
        {index && <FrameBody index={index} />}
      </DiagnosticSection>
    </div>
  );
}

/**
 * O drill-down já filtrado em "Sem disposição" (RF-09). O chip vale por aba, e a
 * pendência pode estar nas duas: abre a primeira que tem, Scope items antes de
 * Desenvolvimentos, na ordem das abas.
 */
function undecidedDetail(index: AdherenceIndexSummary): string {
  const tab = index.rows.process.undecided > 0 ? "scope-items" : "developments";
  return `${DETAIL}?tab=${tab}&status=undecided`;
}

function FrameBody({ index }: { index: AdherenceIndexSummary }) {
  const { t } = useTranslation();
  const { rows, composition } = index;
  const total = rows.consolidated;

  const items = (count: number) => tp(t, count, "{{count}} item", "{{count}} items");
  const processParts = [
    tp(t, composition.scope_items, "{{count}} validated scope item", "{{count}} validated scope items"),
    composition.process_gaps > 0 &&
      tp(t, composition.process_gaps, "{{count}} process GAP", "{{count}} process GAPs"),
    composition.chart_of_accounts_gaps > 0 &&
      tp(
        t,
        composition.chart_of_accounts_gaps,
        "{{count}} chart of accounts GAP",
        "{{count}} chart of accounts GAPs",
      ),
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#3c4b61]">
        <LegendItem color="standard" label={t("Standard")} />
        <LegendItem color="extension" label={t("Standard + Clean Core extension")} />
        <LegendItem color="gap" label={t("GAP (workaround)")} />
      </ul>

      <div>
        <FrameRow
          name="process"
          title={t("Business process")}
          detail={`${items(rows.process.base)} · ${processParts.join(" + ")}`}
          row={rows.process}
        />
        <FrameRow
          name="developments"
          title={t("Developments")}
          detail={`${items(rows.developments.base)} · ${t("WRICEF without an associated scope item")}`}
          row={rows.developments}
        />
        <FrameRow
          name="consolidated"
          title={t("Consolidated")}
          detail={items(total.base)}
          row={total}
          strong
        />
      </div>

      {total.base > 0 && (
        <p
          data-testid="adherence-client-phrase"
          className="rounded-r-md border-l-[3px] border-l-[#ffc83d] bg-[#f2f5f9] px-3.5 py-2.5 text-[13px] leading-relaxed text-[#08254c]"
        >
          {t(
            "Out of every 100 items analysed in the environment, {{standard}} the Public meets in the standard, {{extension}} we meet with a Clean Core extension and {{gap}} need a workaround, each one recorded with the workaround and the acceptance.",
            {
              standard: total.standard.pct,
              extension: total.extension.pct,
              gap: total.gap.pct,
            },
          )}{" "}
          <b>{t("Solution coverage: {{pct}}%.", { pct: total.coverage_pct })}</b>
        </p>
      )}

      <p data-testid="adherence-out-of-count" className="text-[11px] leading-relaxed text-[#7b8799]">
        {tp(
          t,
          rows.developments.excluded,
          "Out of the count: {{count}} development with no usage or from a discontinued process.",
          "Out of the count: {{count}} developments with no usage or from a discontinued process.",
        )}{" "}
        {composition.developments_in_scope_items > 0 && (
          <>
            {tp(
              t,
              composition.developments_in_scope_items,
              "{{count}} development associated with a scope item counts inside Business process (no double counting).",
              "{{count}} developments associated with scope items count inside Business process (no double counting).",
            )}{" "}
          </>
        )}
        {index.gaps.wricef_not_counted > 0 && (
          <>
            {tp(
              t,
              index.gaps.wricef_not_counted,
              "{{count}} GAP from a WRICEF is not counted yet: the development has no “Lack of fit” decision.",
              "{{count}} GAPs from WRICEF are not counted yet: the developments have no “Lack of fit” decision.",
            )}{" "}
          </>
        )}
        {t("Chart of accounts adherence is a separate indicator.")}
      </p>
    </div>
  );
}

function LegendItem({ color, label }: { color: (typeof ADHERENCE_COLORS)[number]; label: string }) {
  return (
    <li className="inline-flex items-center gap-1.5">
      <span aria-hidden className={cn("h-2.5 w-2.5 rounded-sm", ADHERENCE_BAR[color])} />
      {label}
    </li>
  );
}

/**
 * Uma linha do frame. Com base 0 a barra fica vazia e o percentual é o
 * travessão: 0% afirmaria que nada é padrão numa linha sem item contado.
 */
function FrameRow({
  name,
  title,
  detail,
  row,
  strong = false,
}: {
  name: string;
  title: string;
  detail: string;
  row: AdherenceRow;
  strong?: boolean;
}) {
  return (
    <div
      data-testid={`adherence-row-${name}`}
      className="grid grid-cols-1 items-center gap-2 border-b border-[#eef1f5] py-2.5 last:border-b-0 md:grid-cols-[230px_minmax(0,1fr)_270px] md:gap-3.5"
    >
      <div>
        <div className={cn("font-semibold text-[#08254c]", strong ? "text-[15px]" : "text-[13px]")}>
          {title}
        </div>
        <div className="text-[11px] text-[#7b8799]">{detail}</div>
      </div>
      <AdherenceBar row={row} height={strong ? "h-[22px]" : "h-3.5"} />
      <div className="flex flex-wrap gap-3 whitespace-nowrap font-mono text-[12px] text-[#3c4b61]">
        {ADHERENCE_COLORS.map((color) => (
          <span key={color} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={cn("h-2.5 w-2.5 rounded-sm", ADHERENCE_BAR[color])} />
            {formatCount(row[color].count)} · {row[color].pct == null ? NO_VALUE : `${row[color].pct}%`}
          </span>
        ))}
      </div>
    </div>
  );
}
